import React from 'react';
import { Box, Card, CardContent, Container, Grid, Typography } from '@material-ui/core';

const BenefitsCardDeck = ({ sectionConfig = {}, layoutContext = {}, globalConfig = {} }) => {
    const { applyHoverEffect, removeHoverEffect, getIcon, getAnimationProps } = layoutContext;
    const items = sectionConfig.items || globalConfig?.benefits?.items || [];
    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );
    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const titleAnimation = applyAnimation(sectionConfig.animation?.title);

    if (!items.length) {
        return null;
    }

    const gridSpacing = typeof sectionConfig.grid?.spacing === 'number' ? sectionConfig.grid.spacing : 3;
    const defaultGridItem = sectionConfig.gridItem || {};

    const resolveGridValue = (itemValue, fallback) => (typeof itemValue === 'number' ? itemValue : fallback);

    const renderIcon = (benefit) => {
        if (!getIcon) {
            return null;
        }

        const iconContainerStyles = {
            ...(sectionConfig.iconContainerStyles || {}),
            ...(benefit.iconContainerStyles || {}),
            background: benefit.iconColor || benefit.iconBackground || (sectionConfig.iconContainerStyles || {}).background,
            color: benefit.iconForeground || benefit.iconTextColor || (sectionConfig.iconContainerStyles || {}).color,
            boxShadow: benefit.iconShadow || (sectionConfig.iconContainerStyles || {}).boxShadow
        };

        return (
            <Box style={iconContainerStyles}>
                {getIcon(benefit.icon, { fontSize: benefit.iconSize || '1.5rem' })}
            </Box>
        );
    };

    return (
        <Box
            id="benefits-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={{ ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) }}
        >
            <Container maxWidth={sectionConfig.container?.maxWidth || 'lg'} style={sectionConfig.containerStyles || {}}>
                <Box
                    className={titleAnimation.className}
                    data-anim={titleAnimation.dataAnim}
                    data-delay={titleAnimation.dataDelay}
                    data-trigger-once={titleAnimation.dataTriggerOnce}
                    style={{ ...(sectionConfig.titleContainerStyles || {}), ...(titleAnimation.style || {}) }}
                >
                    {sectionConfig.label && (
                        <Typography variant={sectionConfig.typography?.label?.variant || 'body2'} style={sectionConfig.labelStyles || {}}>
                            {sectionConfig.label}
                        </Typography>
                    )}
                    <Typography variant={sectionConfig.typography?.title?.variant || 'h3'} style={sectionConfig.titleStyles || {}}>
                        {sectionConfig.title || globalConfig?.benefits?.title}
                    </Typography>
                    {sectionConfig.subtitle && (
                        <Typography variant={sectionConfig.typography?.subtitle?.variant || 'body1'} style={sectionConfig.subtitleStyles || {}}>
                            {sectionConfig.subtitle}
                        </Typography>
                    )}
                </Box>
                <Grid container spacing={gridSpacing}>
                    {items.map((benefit, index) => {
                        const cardAnimation = applyAnimation(sectionConfig.animation?.items, index);
                        const xs = resolveGridValue(benefit.gridItem?.xs, defaultGridItem.xs || 12);
                        const sm = resolveGridValue(benefit.gridItem?.sm, defaultGridItem.sm || 6);
                        const md = resolveGridValue(benefit.gridItem?.md, defaultGridItem.md || 4);

                        const cardBaseStyles = {
                            ...(sectionConfig.cardStyles || {}),
                            ...(benefit.cardStyles || {}),
                            background: benefit.cardBackground || benefit.background || (sectionConfig.cardStyles || {}).background,
                            borderColor: benefit.cardBorderColor || (sectionConfig.cardStyles || {}).borderColor
                        };

                        const cardContentStyles = {
                            ...(sectionConfig.cardContentStyles || {}),
                            ...(benefit.cardContentStyles || {})
                        };

                        return (
                            <Grid item key={`benefit-card-${index}`} xs={xs} sm={sm} md={md}>
                                <Card
                                    className={cardAnimation.className}
                                    data-anim={cardAnimation.dataAnim}
                                    data-delay={cardAnimation.dataDelay}
                                    data-trigger-once={cardAnimation.dataTriggerOnce}
                                    style={{ ...cardBaseStyles, ...(cardAnimation.style || {}) }}
                                    onMouseEnter={(event) => applyHoverEffect?.(event, benefit.cardHoverStyles || sectionConfig.cardHoverStyles || {})}
                                    onMouseLeave={(event) => removeHoverEffect?.(event, cardBaseStyles)}
                                >
                                    <CardContent style={cardContentStyles}>
                                        {renderIcon(benefit)}
                                        <Typography variant={sectionConfig.typography?.cardTitle?.variant || 'h5'} style={sectionConfig.cardTitleStyles || {}}>
                                            {benefit.title}
                                        </Typography>
                                        <Typography variant={sectionConfig.typography?.cardDescription?.variant || 'body1'} style={sectionConfig.cardDescriptionStyles || {}}>
                                            {benefit.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </Box>
    );
};

export default BenefitsCardDeck;
